import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Building, Briefcase, Users, UserPlus, Check, Search, Filter,
  Pause, TrendingUp, Award, RefreshCw, Send, ShieldCheck, Sparkles,
} from 'lucide-react';
import InvitationDialog from '../InvitationDialog';
import ExportImportBar from '../ExportImportBar';
import useCoopersExperts from './useCoopersExperts';
import CoopersTab from './CoopersTab';
import ExpertsTab from './ExpertsTab';
import CoopersExpertsModals from './CoopersExpertsModals';

export default function GestionCoopersExperts({ getAuthHeader }) {
  const hook = useCoopersExperts({ getAuthHeader });
  const [activeTab, setActiveTab] = React.useState('coopers');

  const totalReseau = (hook.stats.totalCoopers || 0) + (hook.stats.totalExperts || 0);
  const totalActifs = (hook.stats.coopersActifs || 0) + (hook.stats.expertsActifs || 0);
  const tauxActivite = totalReseau > 0 ? Math.round((totalActifs / totalReseau) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="gestion-coopers-experts">
      {/* ============================================ */}
      {/* HERO institutionnel — Réseau COOPER'S & EXPERTS */}
      {/* ============================================ */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 p-6 text-white shadow-xl">
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-yellow-400/10 blur-2xl" />
        <div className="absolute -left-10 bottom-0 w-56 h-56 rounded-full bg-cyan-400/10 blur-2xl" />
        <div className="relative flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 shrink-0">
              <Users className="w-8 h-8 text-yellow-300" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-widest bg-yellow-400/20 text-yellow-200 px-2 py-0.5 rounded-full border border-yellow-300/30">
                  Réseau institutionnel
                </span>
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
              </div>
              <h2 className="text-2xl xl:text-3xl font-bold flex items-center gap-2">
                COOPER&apos;S &amp; EXPERTS
              </h2>
              <p className="text-sm text-blue-100 mt-1 max-w-2xl">
                Pilotage centralisé du réseau : créez, validez, activez ou suspendez les COOPER&apos;S et les Experts qui accompagnent les coopérateurs FTPE sur le terrain.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ExportImportBar collection="coopers" label="COOPER'S" onImported={hook.loadData} />
            <ExportImportBar collection="experts" label="Experts" onImported={hook.loadData} />
            <Button variant="outline" onClick={hook.loadData} disabled={hook.loading} className="bg-white/10 hover:bg-white/20 text-white border-white/30 backdrop-blur-sm" data-testid="btn-refresh">
              <RefreshCw className={`h-4 w-4 mr-2 ${hook.loading ? 'animate-spin' : ''}`} />Actualiser
            </Button>
            <Button onClick={() => hook.setShowCreateExpertModal(true)} className="bg-orange-500 hover:bg-orange-600 shadow-md font-semibold" data-testid="btn-new-expert">
              <UserPlus className="h-4 w-4 mr-2" />Nouvel Expert
            </Button>
            <Button onClick={() => hook.setShowCreateCooperModal(true)} className="bg-purple-500 hover:bg-purple-600 shadow-md font-semibold" data-testid="btn-new-cooper">
              <Building className="h-4 w-4 mr-2" />Nouveau COOPER&apos;S
            </Button>
            <Button onClick={() => { hook.setInvitationRole(null); hook.setShowInvitationDialog(true); }} className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 shadow-md font-bold" data-testid="btn-invitation">
              <Send className="h-4 w-4 mr-2" />Invitation Email
            </Button>
          </div>
        </div>

        {/* Stats inline dans le hero */}
        <div className="relative mt-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <HeroKpi label="COOPER'S" value={hook.stats.totalCoopers} icon={Building} accent="bg-purple-400/25" />
          <HeroKpi label="Actifs" value={hook.stats.coopersActifs} icon={Check} accent="bg-emerald-400/25" />
          <HeroKpi label="Experts" value={hook.stats.totalExperts} icon={Briefcase} accent="bg-orange-400/25" />
          <HeroKpi label="Exp. actifs" value={hook.stats.expertsActifs} icon={Award} accent="bg-cyan-400/25" />
          <HeroKpi label="Suspendus" value={(hook.stats.coopersSuspendus || 0) + (hook.stats.expertsSuspendus || 0)} icon={Pause} accent="bg-amber-400/25" />
          <HeroKpi label="Membres" value={hook.stats.totalMembres} icon={Users} accent="bg-blue-400/25" />
          <HeroKpi label="Taux activité" value={`${tauxActivite}%`} icon={TrendingUp} accent="bg-yellow-400/30" />
        </div>
      </div>

      {/* Bandeau d'information institutionnelle */}
      <div className="rounded-xl border border-purple-200 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 px-4 py-3 flex items-center gap-3 text-sm">
        <Sparkles className="w-4 h-4 text-purple-600 shrink-0" />
        <div className="text-slate-700">
          <strong className="text-slate-900">Bonne pratique :</strong> chaque nouveau COOPER&apos;S ou Expert doit signer une <em>Convention de Partenariat</em> (onglet dédié) avant son activation. Vérifiez aussi qu&apos;il dispose d&apos;un PASS FTPE pour accéder à son espace.
        </div>
      </div>

      {/* Filtres */}
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Rechercher par nom, email, entreprise..." value={hook.searchTerm} onChange={(e) => hook.setSearchTerm(e.target.value)} className="pl-9" data-testid="search-input" />
            </div>
            <Select value={hook.filterStatus} onValueChange={hook.setFilterStatus}>
              <SelectTrigger className="w-48" data-testid="filter-status"><Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Statut" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="actif"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500" />Actifs</span></SelectItem>
                <SelectItem value="suspendu"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" />Suspendus</span></SelectItem>
                <SelectItem value="inactif"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-gray-500" />Inactifs</span></SelectItem>
                <SelectItem value="en_attente"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500" />En attente</span></SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Onglets */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-80">
          <TabsTrigger value="coopers" className="flex items-center gap-2" data-testid="tab-coopers"><Building className="h-4 w-4" />COOPER&apos;S ({hook.filteredCoopers.length})</TabsTrigger>
          <TabsTrigger value="experts" className="flex items-center gap-2" data-testid="tab-experts"><Briefcase className="h-4 w-4" />Experts ({hook.filteredExperts.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="coopers">
          <CoopersTab
            filteredCoopers={hook.filteredCoopers}
            setShowCreateCooperModal={hook.setShowCreateCooperModal}
            setSelectedItem={hook.setSelectedItem}
            setShowDetailModal={hook.setShowDetailModal}
            openEditCooper={hook.openEditCooper}
            updateCooperStatus={hook.updateCooperStatus}
            validateCooper={hook.validateCooper}
            deleteCooper={hook.deleteCooper}
            openResetPasswordModal={hook.openResetPasswordModal}
          />
        </TabsContent>
        <TabsContent value="experts">
          <ExpertsTab
            filteredExperts={hook.filteredExperts}
            setShowCreateExpertModal={hook.setShowCreateExpertModal}
            setSelectedItem={hook.setSelectedItem}
            setShowDetailModal={hook.setShowDetailModal}
            openEditExpert={hook.openEditExpert}
            updateExpertStatus={hook.updateExpertStatus}
            deleteExpert={hook.deleteExpert}
            openResetPasswordModal={hook.openResetPasswordModal}
          />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <CoopersExpertsModals
        coopers={hook.coopers}
        showCreateCooperModal={hook.showCreateCooperModal} setShowCreateCooperModal={hook.setShowCreateCooperModal}
        cooperForm={hook.cooperForm} setCooperForm={hook.setCooperForm}
        showCooperPassword={hook.showCooperPassword} setShowCooperPassword={hook.setShowCooperPassword}
        createCooper={hook.createCooper} generatePassword={hook.generatePassword}
        showCreateExpertModal={hook.showCreateExpertModal} setShowCreateExpertModal={hook.setShowCreateExpertModal}
        expertForm={hook.expertForm} setExpertForm={hook.setExpertForm}
        showExpertPassword={hook.showExpertPassword} setShowExpertPassword={hook.setShowExpertPassword}
        createExpert={hook.createExpert}
        showEditModal={hook.showEditModal} setShowEditModal={hook.setShowEditModal}
        editMode={hook.editMode} saveEdit={hook.saveEdit}
        showDetailModal={hook.showDetailModal} setShowDetailModal={hook.setShowDetailModal}
        selectedItem={hook.selectedItem}
        showResetPasswordModal={hook.showResetPasswordModal} setShowResetPasswordModal={hook.setShowResetPasswordModal}
        resetPasswordForm={hook.resetPasswordForm} setResetPasswordForm={hook.setResetPasswordForm}
        showNewPassword={hook.showNewPassword} setShowNewPassword={hook.setShowNewPassword}
        showConfirmPassword={hook.showConfirmPassword} setShowConfirmPassword={hook.setShowConfirmPassword}
        handleResetPassword={hook.handleResetPassword}
      />

      {/* Dialog Invitation */}
      <InvitationDialog
        isOpen={hook.showInvitationDialog}
        onClose={() => hook.setShowInvitationDialog(false)}
        onSuccess={() => { hook.loadData(); }}
        prefilledRole={hook.invitationRole}
      />
    </div>
  );
}

const HeroKpi = ({ label, value, icon: Icon, accent }) => (
  <div className={`rounded-lg ${accent} backdrop-blur-sm p-3 border border-white/10`}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-blue-100 font-semibold">
      <Icon className="w-3 h-3" />
      <span className="truncate">{label}</span>
    </div>
    <div className="text-xl font-bold mt-0.5 tabular-nums text-white">{value}</div>
  </div>
);
